// src/components/analytics/AnalyticsOverviewPage.tsx
// F1 — cross-channel overview leyendo /admin/analytics/overview.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertCircle, Bot, Eye, Image, LineChart as LineChartIcon,
  Loader2, MessageSquare, MousePointerClick, RefreshCw, Send, Sparkles,
  Target, Users, Zap,
} from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import {
  ApiError, centsToCurrency, formatInt, getOverview, microsToCurrency,
  type AnalyticsOverview, type DailyPoint,
} from '@/lib/api_analytics';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string;
}) => void;

interface Props { addNotification?: Notifier }

const WINDOWS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
];

export function AnalyticsOverviewPage({ addNotification }: Props) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getOverview(days));
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se cargó analytics',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setLoading(false);
    }
  }, [days, addNotification]);

  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    if (!data) return [];
    // merge Meta + Google Ads por fecha en un objeto por día
    const map = new Map<string, any>();
    for (const p of data.timeseries) {
      const row = map.get(p.date) || { date: p.date.slice(5), meta_cents: 0, google_cents: 0, clicks: 0, impressions: 0 };
      if (p.channel === 'meta') {
        row.meta_cents = p.spend_cents;
      } else if (p.channel === 'google_ads') {
        row.google_cents = p.spend_cents;
      }
      row.clicks += p.clicks;
      row.impressions += p.impressions;
      map.set(p.date, row);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={28} className="animate-spin text-emerald-400" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <LineChartIcon className="text-emerald-400" /> Analytics
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Cross-channel overview · {data.window_start} → {data.window_end}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              onClick={() => setDays(w.days)}
              className={`o-btn o-btn-xs ${days === w.days ? 'o-btn-primary' : 'o-btn-ghost'}`}
            >
              {w.label}
            </button>
          ))}
          <button onClick={load} disabled={loading} className="o-btn o-btn-ghost flex items-center gap-2">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Meta + Google Ads KPIs (spend-driven) */}
      <div>
        <div className="text-xs text-gray-400 uppercase mb-2">Ads</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            title="Meta · Gasto"
            value={centsToCurrency(data.channels.meta.spend_cents)}
            sub={`${formatInt(data.channels.meta.impressions)} impresiones`}
            icon={<Activity className="text-sky-400" />}
          />
          <KpiCard
            title="Meta · Clicks"
            value={formatInt(data.channels.meta.clicks)}
            sub={`${formatInt(data.channels.meta.reach)} alcance`}
            icon={<MousePointerClick className="text-sky-400" />}
          />
          <KpiCard
            title="Google Ads · Gasto"
            value={microsToCurrency(data.channels.google_ads.cost_micros)}
            sub={`${formatInt(data.channels.google_ads.impressions)} impresiones`}
            icon={<Target className="text-emerald-400" />}
          />
          <KpiCard
            title="Google Ads · Conversiones"
            value={data.channels.google_ads.conversions?.toFixed(2) ?? '—'}
            sub={`${formatInt(data.channels.google_ads.clicks)} clicks`}
            icon={<Zap className="text-emerald-400" />}
          />
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
          <div className="text-xs text-gray-400 uppercase mb-3">
            Gasto diario por plataforma
          </div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                <Legend />
                <Area
                  type="monotone" dataKey="meta_cents" name="Meta (¢)"
                  stroke="#38bdf8" fillOpacity={0.2} fill="#38bdf8"
                />
                <Area
                  type="monotone" dataKey="google_cents" name="Google Ads (¢)"
                  stroke="#10b981" fillOpacity={0.2} fill="#10b981"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8 text-center text-sm text-gray-500">
          Sin series para el periodo. Los números aparecen cuando los beats de sync de Meta + Google Ads corran (every 30 min).
        </div>
      )}

      {/* WhatsApp */}
      <div>
        <div className="text-xs text-gray-400 uppercase mb-2">WhatsApp</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            title="Inbound"
            value={formatInt(data.channels.whatsapp.inbound)}
            icon={<MessageSquare className="text-emerald-400" />}
          />
          <KpiCard
            title="Outbound enviados"
            value={formatInt(data.channels.whatsapp.outbound_sent)}
            icon={<Send className="text-emerald-400" />}
          />
          <KpiCard
            title="Outbound fallidos"
            value={formatInt(data.channels.whatsapp.outbound_failed)}
            icon={<AlertCircle className="text-red-400" />}
          />
          <KpiCard
            title="Broadcasts cerrados"
            value={formatInt(data.channels.whatsapp.broadcasts_completed)}
            icon={<Users className="text-emerald-400" />}
          />
        </div>
      </div>

      {/* Social publish + AI marketing */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-gray-400 uppercase mb-2">Social publish</div>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard
              title="Publicados"
              value={formatInt(data.channels.publish.published)}
              icon={<Image className="text-sky-400" />}
            />
            <KpiCard
              title="En cola"
              value={formatInt(data.channels.publish.scheduled)}
              icon={<Eye className="text-sky-400" />}
            />
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-400 uppercase mb-2">IA marketing</div>
          <div className="grid grid-cols-3 gap-3">
            <KpiCard
              title="Contenido"
              value={formatInt(data.channels.ai_marketing.content_generated)}
              icon={<Sparkles className="text-purple-400" />}
            />
            <KpiCard
              title="Audiencias"
              value={formatInt(data.channels.ai_marketing.audiences_suggested)}
              icon={<Users className="text-purple-400" />}
            />
            <KpiCard
              title="Alertas"
              value={formatInt(data.channels.ai_marketing.alerts_generated)}
              icon={<Bot className="text-purple-400" />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  title, value, sub, icon,
}: {
  title: string;
  value: string;
  sub?: string;
  icon?: JSX.Element;
}) {
  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
      <div className="flex items-start justify-between">
        <div className="text-xs text-gray-400 uppercase">{title}</div>
        {icon && <span className="opacity-80">{icon}</span>}
      </div>
      <div className="text-2xl text-white font-semibold mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default AnalyticsOverviewPage;
